import React, { memo, useCallback, useId, useState } from "react";

import { useGetDatasetMappersData } from "api/api-hooks/dataset-mapper-hooks";
import { useGetDatasetsInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import DatasetStatusShower from "components/basic/datasetStatusShower/DatasetStatusShower";
import ProductTemplateEntriesDropdown from "components/basic/dropdowns/product-template-entries-dropdown/ProductTemplateEntriesDropdown";
import { DatasetStringValueMapperDTO, LineEditorLoadingTypes } from "components/dashboard/charts/interfaces";
import { useComponentStoreContext } from "components/fragments/dashboard/components/context/ComponentProvider";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useTimeState from "hooks/dashboard/useTimeState";
import useDefaultPlotlyConfig from "hooks/useDefaultPlotlyConfig";
import Plotly, { Annotations, LayoutAxis, ModeBarButton, ModeBarDefaultButtons } from "plotly.js";
import Plot from "react-plotly.js";
import { useResizeDetector } from "react-resize-detector";
import { APIResponse } from "services/HttpService";
import { I, T } from "services/lang/LanguageService";
import { dweenColorway } from "utils/constants";
import calculateYAxisRangeValues from "utils/math-utils";
import { getHoverTemplateValue, isDatasetLoadingConf } from "utils/utils";
import DatasetsTitlesShower from "../DatasetsTitlesShower";
import ExportCsvDialog from "../ExportCsvDialog";
import ChartWrapper from "../chart-wrapper/ChartWrapper";
import copyChartToClipBoard from "../chartUtils";
import {
  annotationIcon,
  clipIcon,
  clipboardIcon,
  csvDownloadIcon,
  forkIcon,
  questionMarkIcon,
  statusIcon,
} from "../icons";
import { AxesConfig, DatasetChartProps, DatasetSpecification, PlotDataEx } from "../interfaces";
import {
  defaultFromDateTime,
  defaultToDateTime,
  splitLongWord,
  splitStringToLines,
} from "../utils";
import DatasetLineChartAnnotations from "./DatasetLineChartAnnotations";
import "./DatasetLineChartStyle.scss";

export interface IYaxisCLipValues {
  identifier: string;
  yAxisMax?: number;
  yAxisMin?: number;
}

const MAX_DTICKS = 25;
const MOBILE_WIDTH = 550;

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
  tickformat: "f",
} as Partial<LayoutAxis>;

const secondaryAxisConfig = {
  ...commonAxisConfig,
  overlaying: "y",
  showgrid: false,
};

const xAxisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

const layoutConfig = {
  legend: { orientation: "h", y: -0.25, x: 0.01, bgcolor: "transparent" },
  colorway: dweenColorway,
  margin: {
    l: 0,
    r: 0,
    b: 20,
    t: 20,
  },
} as Plotly.Layout;

type MapperDataResponse = {
  data: DatasetStringValueMapperDTO[] | undefined;
  isLoading: boolean;
};

const splitMappings = (mappings: { [key: string]: string | number }) => {
  return Object.values(mappings).map(value => {
    const stringValue = String(value);
    const lines = splitStringToLines(stringValue);
    const mappedLines = lines.map((line, index) => {
      if (index === 0 || line.length === 15) {
        return line;
      }
      return splitLongWord(line);
    });
    return mappedLines.join("<br>");
  });
};

const DatasetLineChart = (props: DatasetChartProps) => {
  const uniqueId = useId();
  const identifiers = props.identifiers || props.datasets?.map(dataset => dataset.identifier) || [];
  const { width, ref } = useResizeDetector();
  const setStatus = useComponentStateCallback(props);
  const [axesGrouped, setAxesGrouped] = useState(props.toggleTracesGrouping ?? true);

  const [dialog, setDialog] = useState<boolean>(false);
  const [titlesDialog, setTitlesDialog] = useState<boolean>(false);
  const [exportCsvDialog, setExportCsvDialog] = useState<boolean>(false);

  const [firstData, setFirstData] = useState<DatasetDataInfoResponse[]>([]);
  const [titlesAndOverriddenTitles, setTitlesAndOverriddenTitles] = useState<
    { title: string; overriddenTitle: string }[]
  >([]);
  const [isOverriddenTitleExist, setIsOverriddenTitleExist] = useState<boolean>(false);
  const [dticks, setDticks] = useState<number | undefined>(undefined);
  const [yAxisClipValues, setYAxisClipValues] = useState<Array<IYaxisCLipValues>>([]);
  const [clip, setClip] = useState(false);
  const [hiddenTraces, setHiddenTraces] = useState<Record<string, boolean>>({});
  const [productItemsDateRange, setProductItemsDateRange] = useState<string[]>([]);
  const [isAnnotationsEditorVisible, setIsAnnotationsEditorVisible] = useState<boolean>(false);
  const [axisTickMappings, setAxisTickMappings] = useState<
    Record<string, { tickvals: string[]; ticktext: string[]; title: string }>
  >({});

  const store = useComponentStoreContext();
  const defaultConfig = useDefaultPlotlyConfig();

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const clipAxis = () => {
    setClip(prevState => {
      const newClip = !prevState;
      if (newClip) {
        const yAxisRangeValues = calculateYAxisRangeValues(firstData);
        setYAxisClipValues(yAxisRangeValues);
      } else {
        setYAxisClipValues([]);
      }
      return newClip;
    });
  };

  const handleShowTitles = useCallback(() => {
    setTitlesDialog(true);
  }, []);

  const { data: datasetInfo, isLoading: isDatasetsInfoLoading } = useGetDatasetsInfo(
    identifiers || []
  );

  const { data: mapperDataResponse, isLoading: isMapperDataLoading } =
    useGetDatasetMappersData() as MapperDataResponse;

  const promiseHandler = (res: APIResponse<any>): PlotDataEx[] => {
    const data = res.data.map((ds: DatasetDataInfoResponse) => {
      return mapQueryData(ds);
    });
    setFirstData(res.data);
    return data;
  };

  const showAnnotationsEditor = () => {
    setIsAnnotationsEditorVisible(true);
  };

  const mapQueryData = (dsI: DatasetDataInfoResponse) => {
    const ds = { ...dsI };
    const x: Date[] = [];
    const y: (number | string)[] = [];
    ds.data.forEach(v => {
      x.push(new Date(v.key as string));
      y.push(v.value);
    });

    let line = {};
    if (props.datasets) {
      props.datasets.forEach(dataset => {
        if (dataset.identifier === ds.identifier) {
          line = dataset.line ? dataset.line : {};
          if (dataset.overriddenTitle) {
            ds.title = dataset.overriddenTitle;
            setIsOverriddenTitleExist(true);
            setTitlesAndOverriddenTitles(prevState => [
              ...prevState,
              { title: I(dsI.title), overriddenTitle: I(dataset.overriddenTitle!) },
            ]);
          }
        }
      });
    }

    let mappings: { [key: string]: string | number } = {};
    let mappedY: (string | number)[] = [];
    let { unit } = ds;
    if (
      mapperDataResponse &&
      mapperDataResponse.find(mapper => Number(mapper.id) === ds.stringValueMapper)
    ) {
      const mapper = mapperDataResponse.find(
        singleMapper => Number(singleMapper.id) === ds.stringValueMapper
      );
      if (mapper) {
        const tickvals = Object.keys(mapper.mappings || {});
        const ticktext = splitMappings(mapper.mappings || {});
        axisTickMappings[ds.identifier] = {
          tickvals,
          ticktext,
          title: I(mapper.title),
        };
        setAxisTickMappings(prev => ({
          ...prev,
          [ds.identifier]: {
            tickvals,
            ticktext,
            title: I(mapper.title),
          },
        }));

        mappings = mapper.mappings;
        mappedY = y.map(value => {
          const mappedValue = mappings[value as string] || value;
          if (typeof mappedValue === "string") {
            const lines = splitStringToLines(mappedValue);
            const mappedLines = lines.map((currentLine, index) => {
              if (index === 0 || currentLine.length === 15) {
                return currentLine;
              }
              return splitLongWord(currentLine);
            });
            return mappedLines.join("<br>");
          }
          return mappedValue;
        });
      }
    } else {
      mappedY = y;
    }

    if (
      datasetInfo?.find(dataset => dataset.identifier === ds.identifier)?.stringValueMapper &&
      mappedY.length !== 0
    ) {
      const currentMapperId = datasetInfo.find(
        dataset => dataset.identifier === ds.identifier
      )?.stringValueMapper;
      unit = mapperDataResponse?.find(mapper => mapper.id === currentMapperId)?.title ?? "";
    }

    const customName = `${I(ds.title)}${unit ? ` (${unit})` : ""}`;

    return {
      identifier: ds.identifier,
      name: customName,
      type: "scatter",
      mode: props.chartConf?.mode ? props.chartConf.mode : "lines",
      marker: props.chartConf?.marker ? { ...props.chartConf?.marker } : {},
      unit,
      showlegend: true,
      text: mappedY,
      hovertemplate: getHoverTemplateValue(ds),
      line,
      x,
      y,
    } as PlotDataEx;
  };

  const toggleGroupingHandler = () => {
    setAxesGrouped(currentAxesGrouped => !currentAxesGrouped);
  };

  const showStatus = () => {
    setDialog(true);
  };

  const groupByUnitTraces = (data: Array<Partial<PlotDataEx>>) => {
    const unitMap = new Map<string, string>();
    const axes: AxesConfig = {};
    const domain = [0, 1];
    for (let i = 0; i < data.length; i++) {
      const unit = data[i].unit!!;
      const trace = data[i];
      const range = getRange(data[i].identifier, props.datasets);
      const yAxisMaxValue = clip ? yAxisClipValues[i]?.yAxisMax : undefined;
      const yAxisMinValue = clip ? yAxisClipValues[i]?.yAxisMin : undefined;

      if (!unitMap.has(unit)) {
        const axCount = unitMap.size + 1;

        if (axCount === 1) {
          unitMap.set(unit, "y");
          axes.yaxis = createAxisConfig(
            axCount,
            unit!,
            domain,
            range || undefined,
            yAxisMaxValue,
            yAxisMinValue
          );
        } else {
          unitMap.set(unit, `y${axCount}`);

          axes[`yaxis${axCount}`] = createAxisConfig(
            axCount,
            unit!,
            domain,
            range || undefined,
            yAxisMaxValue,
            yAxisMinValue
          );
        }
      }

      trace.yaxis = unitMap.get(unit)!!;
    }

    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;
    return axes;
  };

  const ungroupTraces = (data: Array<Partial<PlotDataEx>>) => {
    const axes: AxesConfig = {};
    const domain = [0, 1];

    for (let i = 0; i < data.length; i++) {
      const { unit } = data[i];
      const trace = data[i];
      const axis = i + 1;
      const range = getRange(data[i].identifier, props.datasets);
      let yAxisMaxValue;
      let yAxisMinValue;

      if (yAxisClipValues) {
        yAxisMaxValue = clip ? yAxisClipValues[i]?.yAxisMax : undefined;
        yAxisMinValue = clip ? yAxisClipValues[i]?.yAxisMin : undefined;
      }

      if (axis === 1) {
        axes.yaxis = createAxisConfig(
          axis,
          unit!,
          domain,
          range || undefined,
          yAxisMaxValue,
          yAxisMinValue
        );
      } else {
        axes[`yaxis${axis}`] = createAxisConfig(
          axis,
          unit!,
          domain,
          range || undefined,
          yAxisMaxValue,
          yAxisMinValue
        );
        trace.yaxis = `y${axis}`;
      }
    }

    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;

    return axes;
  };

  const createAxisConfig = (
    axis: number,
    unit: string,
    domain: Array<number>,
    range?: Array<number>,
    yAxisMaxValue?: number | undefined,
    yAxisMinValue?: number | undefined
  ): LayoutAxis => {
    if (axis === 1) {
      return {
        title: {
          text: unit,
          standoff: 10,
        },
        range,
        autorangeoptions: {
          clipmax: props.setMaxYAxis ? props.setMaxYAxis : yAxisMaxValue, // max value of y axis
          clipmin: props.setMinYAxis ? props.setMinYAxis : yAxisMinValue, // min value of y axis
        },
        ...commonAxisConfig,
      } as LayoutAxis;
    }
    if (axis === 2) {
      return {
        title: {
          text: unit,
          standoff: 10,
        },
        side: "right",
        anchor: "x",
        range,
        autorangeoptions: {
          clipmax: props.setMaxYAxis ? props.setMaxYAxis : yAxisMaxValue, // max value of y axis
          clipmin: props.setMinYAxis ? props.setMinYAxis : yAxisMinValue, // min value of y axis
        },
        ...secondaryAxisConfig,
      } as LayoutAxis;
    }
    let axOffset;
    const pxVal = 100 / width!!;
    const offVal = pxVal * 0.75;

    // Left
    if (axis % 2 === 0) {
      axOffset = Math.trunc(axis / 2) * offVal - offVal * 2;
      domain[0] = Math.max(0, axOffset) + offVal;
    }
    // Right
    else {
      axOffset = 1 - Math.trunc(axis / 2) * offVal + offVal;

      if (axOffset > 0) {
        domain[1] = axOffset - offVal - offVal / 1.5;
      } else {
        domain[1] = axOffset - offVal - offVal / 0.5;
      }
    }

    return {
      title: {
        text: unit,
        standoff: 4,
      },
      position: axOffset,
      side: "left",
      anchor: "free",
      range,
      ...secondaryAxisConfig,
    } as LayoutAxis;
  };

  const getRange = (identifier?: string, datasets?: DatasetSpecification[]) => {
    if (!datasets || !identifier) return undefined;

    return datasets.find((d: DatasetSpecification) => d.identifier === identifier)?.range;
  };

  const chartConfig: Partial<Plotly.Config> = {
    displayModeBar: props.reportMode ? false : "hover",
    modeBarButtonsToAdd: [
      {
        title: T("chart.config.toggle-y-axis-clip"),
        name: "Toggle Y Axis Clip",
        icon: clipIcon,
        click: clipAxis,
      },
      {
        title: T("chart-config.toggle-trace-grouping"),
        name: "Toggle trace grouping",
        icon: forkIcon,
        click: toggleGroupingHandler,
      },
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      {
        title: T("chart-config.show-statistics"),
        name: "Show Statistics",
        icon: statusIcon,
        click: showStatus,
      },
      store && {
        title: "Annotations",
        name: "Annotations",
        icon: annotationIcon,
        click: showAnnotationsEditor,
      },
      {
        title: `${T("action.download")} CSV`,
        name: "Download CSV",
        icon: csvDownloadIcon,
        click: () => setExportCsvDialog(true),
      },
      isOverriddenTitleExist && {
        title: T("chart-config.show-titles"),
        name: "Show Titles",
        icon: questionMarkIcon,
        click: handleShowTitles,
      },
    ].filter(Boolean) as ModeBarDefaultButtons[] | ModeBarButton[] | undefined,
  };

  const findYValueAtTime = (data: InstantDoubleValue[], selectedDate: Date | null) => {
    if (!data || data.length === 0 || !selectedDate) return null;

    const selectedDateTime = selectedDate.getTime();

    const closestPoint = data.reduce((prev, curr) => {
      const prevTime = new Date(prev.key).getTime();
      const currTime = new Date(curr.key).getTime();

      const prevDiff = Math.abs(prevTime - selectedDateTime);
      const currDiff = Math.abs(currTime - selectedDateTime);

      return currDiff < prevDiff ? curr : prev;
    });
    return closestPoint ? closestPoint.value : null;
  };

  const from =
    timeState.from ||
    defaultFromDateTime({
      loadConf: isDatasetLoadingConf(props.loadConf) ? props.loadConf : undefined,
      referenceTime: props.referenceTime,
    });

  const to =
    timeState.from ||
    defaultToDateTime({
      loadConf: isDatasetLoadingConf(props.loadConf) ? props.loadConf : undefined,
      referenceTime: props.referenceTime,
    });

  const plotAnnotations = props.annotations
    ?.filter(annotation => {
      const annotationDate = annotation.date ? new Date(annotation.date) : null;
      return annotationDate && annotationDate >= from && annotationDate <= to;
    })
    .map(annotation => {
      const dataset = firstData.find(d => d.identifier === annotation.dataset);
      const yValue = findYValueAtTime(dataset?.data ?? [], new Date(annotation.date!));
      return {
        x: annotation.date ? new Date(annotation.date) : null,
        y: yValue,
        text: I(annotation.text),
        showarrow: true,
        arrowhead: 7,
        ax: 0,
        ay: -40,
      };
    });

  const handleLegendClick = (
    event: Readonly<Plotly.LegendClickEvent>,
    plotData: DatasetDataInfoResponse[]
  ) => {
    const { curveNumber } = event;

    if (plotData[curveNumber]) {
      const { identifier } = plotData[curveNumber];

      setHiddenTraces(prevHiddenTraces => ({
        ...prevHiddenTraces,
        [identifier as string]: !prevHiddenTraces[identifier as string],
      }));
    }

    return false;
  };

  const getUpdatedData = (data: PlotDataEx[]) => {
    return data.map(trace => ({
      ...trace,
      visible: hiddenTraces[trace.identifier as string] ? "legendonly" : true,
    }));
  };

  return (
    <div className="dataset-line-chart overflow-hidden h-full" ref={ref}>
      {props.loadConf?.type === LineEditorLoadingTypes.PRODUCT_ENTRY && props.loadConf.refName && (
        <ProductTemplateEntriesDropdown
          safeName={props.loadConf.refName}
          dateFrom={props.productTemplateCreationDate ?? new Date()}
          startAttribute={props.loadConf.startAttribute}
          endAttribute={props.loadConf.endAttribute ?? ""}
          setProtuctItemsDateRange={setProductItemsDateRange}
        />
      )}
      {(!isDatasetsInfoLoading || (!isMapperDataLoading && datasetInfo)) && (
        <ChartWrapper
          title=""
          uniqueKey={uniqueId}
          identifiers={props.datasets ? props.datasets?.map(d => d.identifier) : identifiers}
          showDateSelection={props.showDateSelection ?? false}
          promiseHandler={promiseHandler}
          axesConfig={axesGrouped ? groupByUnitTraces : ungroupTraces}
          chartConfig={chartConfig}
          timeAxisStepSeconds={props.timeAxisStepSeconds}
          maxDTicks={MAX_DTICKS}
          dticks={dticks}
          setDticks={setDticks}
          updateFrequency={props.updateFrequency}
          reportMode={props.reportMode}
          height={props.height}
          loadConf={props.loadConf}
          hideRefresh={props.hideRefresh}
          dynamicHeight={props.dynamicHeight}
          referenceTime={props.referenceTime}
          chartDialogOpen={props.chartDialogOpen}
          width={props.width}
          dateFrom={timeState.from}
          dateTo={timeState.to}
          hideHeader={props.hideHeader}
          dateAndRefreshButtonsVisible={props.dateAndRefreshButtonsVisible}
          productItemsDateRange={productItemsDateRange}
        >
          {chartProps => (
            <>
              {dialog && (
                <DatasetStatusShower dialog={dialog} setDialog={setDialog} datasets={firstData} />
              )}
              {titlesDialog && (
                <DatasetsTitlesShower
                  dialog={titlesDialog}
                  setDialog={setTitlesDialog}
                  titlesAndOverriddenTitles={titlesAndOverriddenTitles}
                />
              )}
              {exportCsvDialog && (
                <ExportCsvDialog
                  dialog={exportCsvDialog}
                  setDialog={setExportCsvDialog}
                  data={chartProps.data}
                  chartType="LINE"
                />
              )}
              <Plot
                onLegendClick={event => handleLegendClick(event, firstData)}
                className="ea-plotly-chart"
                config={{
                  ...defaultConfig,
                  ...chartProps.chartConfig,
                }}
                data={getUpdatedData(chartProps.data) as PlotDataEx[]}
                layout={{
                  ...chartProps.axesConfig,

                  ...layoutConfig,
                  margin: { t: 20, b: 0, l: 0, r: 0, pad: 5 },
                  xaxis: { ...chartProps.axesConfig?.xaxis, dtick: dticks },
                  ...Object.entries(chartProps.axesConfig as Record<string, LayoutAxis>).reduce(
                    (acc: Record<string, LayoutAxis>, [axisKey, axisConfig]) => {
                      const trace = chartProps.data.find(d => {
                        if (axisKey === "yaxis") return !d.yaxis || d.yaxis === "y";
                        return d.yaxis === axisKey.replace("axis", "");
                      });

                      const identifier = trace?.identifier;
                      const mapping = identifier ? axisTickMappings[identifier] : undefined;

                      acc[axisKey] = {
                        ...axisConfig,
                        ...(mapping && {
                          tickvals: mapping.tickvals,
                          ticktext: mapping.ticktext,
                          title: { text: mapping.title },
                        }),
                      };

                      return acc;
                    },
                    {}
                  ),
                  width: chartProps.width,
                  height: chartProps.height,
                  annotations: plotAnnotations as Partial<Annotations>[] | undefined,
                  showlegend: window.outerWidth > MOBILE_WIDTH,
                  uirevision: "dataset-line-chart-uirevision",
                }}
                onInitialized={() => setStatus({ state: "finished" })}
              />
            </>
          )}
        </ChartWrapper>
      )}
      {isAnnotationsEditorVisible && store && (
        <DatasetLineChartAnnotations
          store={store}
          annotations={props.annotations}
          isDialogVisible={isAnnotationsEditorVisible}
          setIsDialogVisible={setIsAnnotationsEditorVisible}
          datasets={props.datasets || []}
        />
      )}
    </div>
  );
};

export default memo(DatasetLineChart);
